import React, { useState, useEffect } from 'react';
import { Table, Tag, Select, Button, Modal, Input, message, Space } from 'antd';
import client from '../api/client';

const LANGUAGE_LABELS = {
  fa: 'فارسی',
  en: 'EN',
  ja: 'JA',
  de: 'DE',
  pt: 'PT',
  fr: 'FR',
  it: 'IT',
  es: 'ES',
  ko: 'KO',
  hi: 'HI',
  zh: 'ZH'
};

const languageLabel = (lang) => LANGUAGE_LABELS[lang] || lang || '—';

const STATUS_COLORS = {
  pending: 'orange',
  approved: 'blue',
  rejected: 'red',
  implemented: 'green'
};

const STATUS_LABELS = {
  pending: 'در انتظار',
  approved: 'تایید',
  rejected: 'رد',
  implemented: 'پیاده‌سازی‌شده'
};

export default function FeatureRequestManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    load();
  }, [page, statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const res = await client.get('/feature-requests/admin/all', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || {});
    } catch (err) {
      message.error('خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item) => {
    setSelected(item);
    setNewStatus(item.status);
    setAdminNotes(item.adminNotes || '');
    setModalOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await client.patch(`/feature-requests/${selected._id}/status`, {
        status: newStatus,
        adminNotes
      });
      message.success('وضعیت به‌روز شد');
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.message || 'خطا');
    }
  };

  const columns = [
    {
      title: 'عنوان',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <strong>{text}</strong>
          {record.bookReference && (
            <div style={{ fontSize: '12px', color: '#999', marginTop: 2 }}>
              📖 {record.bookReference}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'فرستنده',
      key: 'submitter',
      render: (_, record) => (
        <div style={{ fontSize: '13px' }}>
          {record.submitterName || '—'}
          {record.submitterEmail && (
            <div style={{ color: '#999', fontSize: '11px' }}>{record.submitterEmail}</div>
          )}
        </div>
      )
    },
    { title: 'زبان', dataIndex: 'lang', key: 'lang', width: 70,
      render: l => <Tag>{languageLabel(l)}</Tag> },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: s => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s] || s}</Tag>
    },
    {
      title: 'تاریخ',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      render: d => new Date(d).toLocaleDateString('fa-IR')
    },
    {
      title: 'عملیات',
      key: 'action',
      width: 90,
      render: (_, record) => (
        <Button size="small" onClick={() => openModal(record)}>بررسی</Button>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <Space>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <Tag key={k} color={STATUS_COLORS[k]}>
              {v}: {counts[k] || 0}
            </Tag>
          ))}
        </Space>
        <Select
          placeholder="فیلتر وضعیت"
          allowClear
          style={{ width: 160 }}
          value={statusFilter || undefined}
          onChange={v => { setStatusFilter(v || ''); setPage(1); }}
          options={[
            { value: 'pending', label: 'در انتظار' },
            { value: 'approved', label: 'تایید' },
            { value: 'rejected', label: 'رد' },
            { value: 'implemented', label: 'پیاده‌سازی‌شده' }
          ]}
        />
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          onChange: setPage,
          showSizeChanger: false
        }}
        size="small"
        expandable={{
          expandedRowRender: record => (
            <div style={{ padding: '12px 20px', background: '#fafafa' }}>
              <p><strong>توضیحات:</strong></p>
              <p style={{ whiteSpace: 'pre-wrap', color: '#555' }}>{record.description}</p>
              {record.adminNotes && (
                <p style={{ color: '#1890ff' }}><strong>یادداشت ادمین:</strong> {record.adminNotes}</p>
              )}
            </div>
          )
        }}
      />

      <Modal
        title="بررسی درخواست"
        open={modalOpen}
        onOk={handleUpdate}
        onCancel={() => setModalOpen(false)}
        okText="ثبت تغییرات"
        cancelText="لغو"
      >
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <strong>{selected.title}</strong>
              <p style={{ color: '#666', fontSize: '13px', marginTop: 6 }}>{selected.description}</p>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6 }}>وضعیت:</label>
              <Select
                value={newStatus}
                onChange={setNewStatus}
                style={{ width: '100%' }}
                options={[
                  { value: 'pending', label: 'در انتظار' },
                  { value: 'approved', label: 'تایید' },
                  { value: 'rejected', label: 'رد' },
                  { value: 'implemented', label: 'پیاده‌سازی‌شده' }
                ]}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6 }}>یادداشت ادمین (اختیاری):</label>
              <Input.TextArea
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
                rows={3}
                placeholder="پاسخ یا توضیحات برای کاربر"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
